import { Button, ButtonRow, Icon, Tooltip as NewTooltip, PopoverConfirm } from '@timelyapp/react';
import classNames from 'classnames';
import DurationFormat from 'components/DurationFormat';
import DurationRealtimeFormat from 'components/DurationRealtimeFormat';
import { Brain, InfoIcon } from 'components/Icons';
import RichTooltip from 'components/RichTooltip';
import Tooltip from 'components/Tooltip';
import {
  HOUR_MINUTES_POSTFIXED_DATE_FORMAT,
  MINUTES_POSTFIXED_DATE_FORMAT,
} from 'constants/datetime';
import { SUGGESTED_TIME_TOOLTIP } from 'constants/hour';
import { Duration as LuxonDuration } from 'luxon';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import type { SetHourDraftSuggestedDuration } from 'redux/modules/hours.actions';
import type { getWeeklyCapacityProps, HourEditorProps } from 'redux/selectors/hours.draft';
import type { HourEditorSuggestedDurations } from 'types/hour';
import type { Duration } from 'types/units';
import { isContrastReadable } from 'utils/colors';
import { Label } from './Label';
import { TimeSectionWrap } from './ScrollSyncComponents';
import { Section } from './Section';
import styles from './TimeSection.module.scss';
import { DurationGroup } from './time-input';

interface TimeSectionProps {
  duration: Duration;
  disabled: boolean;
  estimatedDuration: Duration;
  onChange: (hours: number, minutes: number) => void;
  onChangeEstimated: (hours: number, minutes: number) => void;
  onChangeToSuggestedTime: (setHourDraftSuggestedDuration: SetHourDraftSuggestedDuration) => void;
  onToggleEstimatedDuration: VoidFunction;
  adjustHourDraftDuration: (duration: number) => void;
  showEstimatedDuration: boolean;
  backgroundColor: string;
  timerStartedOn: number;
  suggestedDurations: HourEditorSuggestedDurations;
  standalone?: boolean;
  suggesterId?: number;
  noBottom?: boolean;
  scrollTop: number;
  scrollOffset: number;
  weeklyCapacityDetails: ReturnType<typeof getWeeklyCapacityProps>;
  ui: HourEditorProps['ui'];
  isTimerDisabled?: boolean;
  onToggleTimer: () => void;
  isTimerRunning: boolean;
  isTimerHidden: boolean;
  isSaveDisabled: boolean;
}

export const TimeSection = React.memo((props: TimeSectionProps) => {
  function handleChange(hours: number, minutes: number) {
    if (props.ui.isLocked) {
      return;
    }
    props.onChange(hours, minutes);
  }

  function handleChangeEstimated(hours: number, minutes: number) {
    if (props.ui.isLocked) {
      return;
    }
    props.onChangeEstimated(hours, minutes);
  }

  function handleAdjustDuration() {
    if (props.ui.isLocked) {
      return;
    }
    props.adjustHourDraftDuration(weeklyCapacityDetails.overCapacityAmount);
  }

  const {
    ui,
    standalone,
    disabled,
    duration,
    estimatedDuration,
    onChangeToSuggestedTime,
    showEstimatedDuration,
    timerStartedOn,
    backgroundColor,
    suggestedDurations,
    noBottom,
    weeklyCapacityDetails,
    isTimerDisabled,
    onToggleTimer,
    isTimerRunning,
    isTimerHidden,
    isSaveDisabled,
  } = props;
  const needsLightText = isContrastReadable('#fff', backgroundColor);
  const style = { backgroundColor };

  const containerClass = classNames(styles.container, {
    [styles.darkText]: !needsLightText,
    [styles.isBasedOnSuggestion]: ui.isBasedOnSuggestion,
    [styles.isStandalone]: standalone,
    [styles.noBottom]: noBottom,
    [styles.noProject]: !ui.hasSelectedProject,
    [styles.showBorder]: props.scrollTop > 130,
  });
  const isSuggestedDurationsDisabled = disabled || !!timerStartedOn;
  const variant = !needsLightText ? 'dark' : 'light';
  const timerButton = (!isTimerHidden || isTimerRunning) && (
    <NewTooltip tooltip={isTimerRunning ? 'Pause timer' : 'Start timer'}>
      <Button
        onClick={onToggleTimer}
        disabled={isSaveDisabled || isTimerDisabled}
        data-testid="Timer_action_section"
        size="small"
        priority="low"
        className={classNames(styles.timerButton, styles[`variant-${variant || 'default'}`])}
      >
        {isTimerRunning ? (
          <Icon name="PauseSquare" type="solid" />
        ) : (
          <Icon name="ClockStopwatch" type="solid" />
        )}
      </Button>
    </NewTooltip>
  );

  return (
    <React.Fragment>
      <div className={containerClass} style={style} data-testid="hour-editor_time-section">
        <div
          className={classNames(styles.primaryRow, { [styles.timerRunning]: ui.isTimerRunning })}
        >
          {ui.isTimerRunning ? (
            <React.Fragment>
              <Label>Running Timer</Label>
              <DurationRealtimeFormat
                className={classNames(
                  styles.runningTimer,
                  styles[`variant-${variant || 'default'}`]
                )}
                timerStartedOn={timerStartedOn}
                value={duration.total_seconds}
                valueType="seconds"
                format="h[h] mm[m] ss[s]"
                timerButton={timerButton}
                alignItems="center"
              />
            </React.Fragment>
          ) : (
            <DurationGroup
              containerClass={styles.loggedHoursLabel}
              disabled={disabled}
              variant={variant}
              label="Logged time"
              // infoTooltip="@todo this message?"
              duration={duration}
              onChange={handleChange}
              timerButton={timerButton}
            />
          )}
          {weeklyCapacityDetails.isOverRemainingCapacity && (
            <div className={styles.capacityTooltip}>
              <PopoverConfirm
                prompt={`You're exceeding your weekly capacity by ${LuxonDuration.fromObject({
                  hours: weeklyCapacityDetails.overCapacityAmount,
                }).toFormat("h'h' mm'm'")}`}
                confirmLabel="Adjust"
                cancelLabel="Cancel"
                onConfirm={handleAdjustDuration}
                position="right"
                target={
                  <Button
                    className={styles.capacityInfoButton}
                    onClick={handleAdjustDuration}
                    priority="low"
                    size="medium"
                    caret={false}
                  >
                    <InfoIcon />
                  </Button>
                }
              />
            </div>
          )}
        </div>
        <ButtonRow>
          {suggestedDurations.map(({ key, isActive, seconds, isBasedOnMemories }) => {
            const buttonToRender = (
              <Button
                asChild
                data-testid="suggested_duration_time_section"
                disabled={isSuggestedDurationsDisabled}
                size="xsmall"
                onClick={event => {
                  event.preventDefault();
                  if (!isSuggestedDurationsDisabled) {
                    onChangeToSuggestedTime({ seconds });
                  }
                }}
                // Prevent focus steal so we can still submit with [Enter]
                onMouseDown={event => event.preventDefault()}
              >
                <button
                  style={{
                    ['--color' as any]: needsLightText ? '#fff' : '#000',
                    color: 'var(--color)',
                    background: 'transparent',
                  }}
                  className={classNames(styles.durationSuggestionButton, {
                    [styles.suggestionActive]: !isSuggestedDurationsDisabled && isActive,
                  })}
                >
                  {isBasedOnMemories && <Brain />}
                  <DurationFormat
                    format={
                      seconds < 3600
                        ? MINUTES_POSTFIXED_DATE_FORMAT
                        : HOUR_MINUTES_POSTFIXED_DATE_FORMAT
                    }
                    value={seconds}
                    valueType="seconds"
                  />
                </button>
              </Button>
            );

            if (isBasedOnMemories) {
              return (
                <RichTooltip key={key} fixed position="top-end" tooltip={SUGGESTED_TIME_TOOLTIP}>
                  {buttonToRender}
                </RichTooltip>
              );
            }

            return <React.Fragment key={key}>{buttonToRender}</React.Fragment>;
          })}
        </ButtonRow>
      </div>
      <TimeSectionWrap name="timeSection" scrollTop={props.scrollTop} enabled={!standalone}>
        {showEstimatedDuration ? (
          <Section standalone={standalone} className={styles.plannedTimeSection}>
            <DurationGroup
              label="Planned time"
              inline
              size="small"
              variant="dark"
              namePrefix="estimated"
              duration={estimatedDuration}
              onChange={handleChangeEstimated}
              disabled={disabled}
            />
            <Tooltip
              className={styles.clear}
              position="left"
              title="Clear planned time"
              onClick={props.onToggleEstimatedDuration}
            >
              <FontAwesome name="times" />
            </Tooltip>
          </Section>
        ) : null}
      </TimeSectionWrap>
    </React.Fragment>
  );
});
